// src/pages/OurJourney.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import { 
  FaArrowLeft, 
  FaBookOpen, 
  FaUserGraduate, 
  FaLightbulb, 
  FaRocket, 
  FaHeart, 
  FaQuoteLeft, 
  FaProductHunt, 
  FaCode 
} from "react-icons/fa";
import "aos/dist/aos.css";
import "./OurJourney.css";

function OurJourney() {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 900, once: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="oj-page">
      {/* Background Glows */}
      <div className="oj-bg-glow-1"></div>
      <div className="oj-bg-glow-2"></div>

      {/* Header */}
      <header className="oj-header">
        <div className="oj-logo" onClick={() => navigate("/")}>
          🧠 Bodh<span className="accent">AI</span>
        </div>
        <button className="oj-back-btn" onClick={() => navigate("/")}>
          <FaArrowLeft /> Back Home
        </button>
      </header>

      {/* Main Container */}
      <main className="oj-container">
        {/* Hero Section */}
        <section className="oj-hero" data-aos="fade-up">
          <span className="oj-hero-badge">
            <FaBookOpen /> The Official Founder Story
          </span>
          <h1 className="oj-hero-title">
            Our Journey: Building <span>BodhAI</span>
          </h1>
          <p className="oj-hero-subtitle">
            From a late-night idea in a student dorm to launching BodhAI V2 Beta live on Product Hunt. 
            Here is our story of building an AI-powered learning platform in public.
          </p>
        </section>

        {/* Quick Stats Bar */}
        <div className="oj-stats-bar" data-aos="fade-up">
          <div className="oj-stat-card">
            <div className="oj-stat-number">100%</div>
            <div className="oj-stat-label">Solo Founded & Built</div>
          </div>
          <div className="oj-stat-card">
            <div className="oj-stat-number">V2 Beta</div>
            <div className="oj-stat-label">Product Hunt Launch</div>
          </div>
          <div className="oj-stat-card">
            <div className="oj-stat-number">Real-Time</div>
            <div className="oj-stat-label">Monaco & Judge0 Compiler</div>
          </div>
        </div>

        {/* Section 1: Meet Vivek */}
        <div className="oj-card-block" data-aos="fade-up">
          <div className="oj-card-header">
            <div className="oj-card-icon">
              <FaUserGraduate />
            </div>
            <h2 className="oj-card-title">Meet Vivek</h2>
          </div>
          <div className="oj-card-body">
            <p>
              Hi, I'm Vivek — computer science student, developer, and creator behind BodhAI.
            </p>
            <p>
              As a student preparing for technical roles, I experienced firsthand how clunky and disconnected traditional examination platforms were. Most existing sites either offered static multiple-choice questions or heavy, expensive code editors that lagged during live assessments.
            </p>
          </div>
        </div>

        {/* Section 2: Why I Built BodhAI */}
        <div className="oj-card-block" data-aos="fade-up">
          <div className="oj-card-header">
            <div className="oj-card-icon">
              <FaLightbulb />
            </div>
            <h2 className="oj-card-title">Why I Built BodhAI</h2>
          </div>
          <div className="oj-card-body">
            <p>
              I set out to create an end-to-end learning space where students could practice technical aptitude, solve real coding problems, and analyze their growth under proctored exam conditions.
            </p>

            <div className="oj-quote-box">
              <FaQuoteLeft className="oj-quote-icon" />
              <div>
                "True mastery comes from targeted practice, immediate feedback, and real-world tools — not just memorizing answers."
              </div>
            </div>

            <p>
              BodhAI is built on three key pillars: high-speed code execution via Monaco & Judge0, intelligent tab-security monitoring, and instant AI question generation.
            </p>
          </div>
        </div>

        {/* Section 3: Roadmap Evolution */}
        <div className="oj-card-block" data-aos="fade-up">
          <div className="oj-card-header">
            <div className="oj-card-icon">
              <FaRocket />
            </div>
            <h2 className="oj-card-title">Milestones & Product Evolution</h2>
          </div>
          <div className="oj-card-body">
            <div className="oj-timeline-wrapper">
              <div className="oj-timeline-item">
                <span className="oj-timeline-step">STEP 01</span>
                <div className="oj-timeline-content">
                  <h4>BodhAI V1 Prototype</h4>
                  <p>Initial release with MCQ practice, basic Judge0 execution, and live teacher monitoring panels.</p>
                </div>
              </div>

              <div className="oj-timeline-item">
                <span className="oj-timeline-step">STEP 02</span>
                <div className="oj-timeline-content">
                  <h4>BodhAI V2 Beta & Product Hunt</h4>
                  <p>Unified exam engine (`VidyaPariksha`), React 19 performance optimizations, glassmorphism theme, and Product Hunt debut.</p>
                </div>
              </div>

              <div className="oj-timeline-item">
                <span className="oj-timeline-step">STEP 03</span>
                <div className="oj-timeline-content">
                  <h4>BodhAI v2.0 & Mobile Ecosystem</h4>
                  <p>Personalized AI tutors, adaptive difficulty algorithms, and native mobile proctoring applications.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Product Hunt & Community */}
        <div className="oj-card-block" data-aos="fade-up">
          <div className="oj-card-header">
            <div className="oj-card-icon">
              <FaHeart style={{ color: "#ff6154" }} />
            </div>
            <h2 className="oj-card-title">Product Hunt & Community</h2>
          </div>
          <div className="oj-card-body">
            <p>
              Launching on Product Hunt is a major milestone. Building in public as a solo developer has been challenging, but the feedback from early users has been incredible.
            </p>
            <p>
              Thank you to every student, teacher, and mentor who tested early builds and helped push BodhAI forward!
            </p>
          </div>
        </div>

        {/* Call To Action Block */}
        <div className="oj-cta-block" data-aos="zoom-in">
          <h2 className="oj-cta-title">Support BodhAI V2 Beta</h2>
          <p className="oj-cta-desc">
            Help us reach more students and developers by supporting our launch on Product Hunt!
          </p>

          <div className="oj-cta-actions">
            <a
              href="https://www.producthunt.com/products/bodhai?utm_source=bodhai.pages.dev&utm_medium=referral&utm_campaign=bodhai_product_launch_july_2026&utm_content=visit_producthunt_from_bodhai_homepage&utm_term=ai_learning_tool_launch"
              target="_blank"
              rel="noopener noreferrer"
              className="oj-cta-btn light"
            >
              <FaProductHunt /> Upvote on Product Hunt
            </a>
            <a
              href="https://betav2.bodhai.pages.dev/"
              target="_blank"
              rel="noopener noreferrer"
              className="oj-cta-btn dark"
            >
              <FaCode /> Try V2 Beta
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}

export default OurJourney;
